import React, { useState, useMemo, useEffect } from "react";
import { X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { formatCurrency, normalizeAdName } from "../utils/helpers";
import { ROWS_PER_PAGE } from "../utils/constants";
import Card from "./ui/Card";
import Button from "./ui/Button";
import { temporary_fix_disable_commission } from "../utils/featureFlags";

const AffiliateDetailModal = ({ affiliate, onClose, commissionPercentage, isLoading = false }) => {
  const [sortConfig, setSortConfig] = useState({
    key: "commission",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const sortedAds = useMemo(() => {
    // Handle case where ads might not be loaded yet
    if (!affiliate.ads || affiliate.ads.length === 0) {
      return [];
    }

    // Group ads by normalized name and combine their metrics
    const adGroups = new Map();

    affiliate.ads.forEach((ad) => {
      const normalizedName = normalizeAdName(ad.adName);

      if (!adGroups.has(normalizedName)) {
        adGroups.set(normalizedName, {
          adName: normalizedName,
          adSpend: 0,
          revenue: 0,
          profit: 0,
          commission: 0,
          purchases: 0,
          instances: 0,
        });
      }

      const group = adGroups.get(normalizedName);
      group.adSpend += ad.adSpend;
      group.revenue += ad.revenue;
      group.profit += ad.profit;
      group.commission += ad.commission;
      group.purchases += ad.purchases;
      group.instances += 1;
    });

    // Convert to array and calculate ROAS for each group
    const groupedAds = Array.from(adGroups.values()).map((group) => ({
      ...group,
      roas: group.adSpend > 0 ? group.revenue / group.adSpend : 0,
    }));

    // Sort the grouped data
    if (sortConfig.key) {
      groupedAds.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (sortConfig.direction === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }
    return groupedAds;
  }, [affiliate.ads, sortConfig]);

  // Paginate the data
  const paginatedAds = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return sortedAds.slice(startIndex, endIndex);
  }, [sortedAds, currentPage]);

  const totalPages = Math.ceil(sortedAds.length / ROWS_PER_PAGE);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "desc"
          ? "asc"
          : "desc",
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "desc" ? (
        <ArrowDown className="h-4 w-4 ml-1 inline" />
      ) : (
        <ArrowUp className="h-4 w-4 ml-1 inline" />
      );
    }
    return <ArrowUpDown className="h-4 w-4 ml-1 inline opacity-30" />;
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-black/50 backdrop-blur-2xl border border-white/10 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-black/20 backdrop-blur-xl rounded-t-3xl border-b border-white/10">
          <div className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {affiliate.name} Performance
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/20 border-t-[#B8460E] rounded-full animate-spin"></div>
                      Loading individual ad data...
                    </span>
                  ) : (
                    `Showing ${paginatedAds.length} of ${sortedAds.length} unique ads
                    (${affiliate.ads?.length || 0} total instances)`
                  )}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="px-6 pb-6">
            <div className={`grid grid-cols-1 md:grid-cols-2 ${temporary_fix_disable_commission ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4`}>
              <Card className="p-4 bg-white/5 border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Total Spend</p>
                <p className="text-xl font-bold text-white">
                  {formatCurrency(affiliate.totalAdSpend)}
                </p>
              </Card>
              <Card className="p-4 bg-white/5 border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Total Revenue</p>
                <p className="text-xl font-bold text-white">
                  {formatCurrency(affiliate.totalRevenue)}
                </p>
              </Card>
              <Card className="p-4 bg-white/5 border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Average ROAS</p>
                <p className="text-xl font-bold text-white">
                  {affiliate.avgROAS.toFixed(2)}
                </p>
              </Card>
              {!temporary_fix_disable_commission && (
                <Card className="p-4 bg-white/5 border border-white/10">
                  <p className="text-sm text-gray-400 mb-1">Total Commission</p>
                  <p className="text-xl font-bold text-[#B8460E]">
                    {formatCurrency(affiliate.totalCommission)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    <span className="text-cyan-400 font-semibold">
                      {affiliate.avgCommissionRate
                        ? `${(affiliate.avgCommissionRate * 100).toFixed(1)}%`
                        : "0.0%"}
                    </span>{" "}
                    avg rate (tiered system)
                  </p>
                </Card>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <Card className="overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-white/20 border-t-[#B8460E] rounded-full animate-spin mb-4"></div>
                <span className="text-gray-400 text-lg font-medium">Loading individual ad data...</span>
                <span className="text-gray-500 text-sm mt-2">Fetching detailed performance data for {affiliate.name}</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {sortedAds.length === 0 ? (
                  <div className="flex flex-col justify-center items-center h-64">
                    <span className="text-gray-400 text-lg font-medium">No individual ad data found</span>
                    <span className="text-gray-500 text-sm mt-2">No ads were found for {affiliate.name} with the current filters</span>
                  </div>
                ) : (
                  <table className="min-w-full">
                  <thead className="sticky top-0 z-10 bg-black/30 backdrop-blur-xl">
                    <tr>
                      <th
                        className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("adName")}
                      >
                        Ad Name
                        <SortIndicator columnKey="adName" />
                      </th>
                      <th
                        className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("adSpend")}
                      >
                        Spend
                        <SortIndicator columnKey="adSpend" />
                      </th>
                      <th
                        className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("revenue")}
                      >
                        Revenue
                        <SortIndicator columnKey="revenue" />
                      </th>
                      <th
                        className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("roas")}
                      >
                        ROAS
                        <SortIndicator columnKey="roas" />
                      </th>
                      <th
                        className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("purchases")}
                      >
                        Purchases
                        <SortIndicator columnKey="purchases" />
                      </th>
                      {!temporary_fix_disable_commission && (
                        <th
                          className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors whitespace-nowrap"
                          onClick={() => handleSort("commission")}
                        >
                          Commission
                          <SortIndicator columnKey="commission" />
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {paginatedAds.map((ad, index) => (
                      <tr
                        key={`${currentPage}-${index}`}
                        className="hover:bg-white/5 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-white">
                          <div className="max-w-xs truncate" title={ad.adName}>
                            {ad.adName}
                            {ad.instances > 1 && (
                              <span className="ml-2 text-xs text-gray-300 bg-white/10 px-2 py-1 rounded-full">
                                {ad.instances} instances
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                          {formatCurrency(ad.adSpend)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                          {formatCurrency(ad.revenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                          {ad.roas.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                          {ad.purchases}
                        </td>
                        {!temporary_fix_disable_commission && (
                          <td
                            className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${
                              ad.commission >= 0 ? "text-green-400" : "text-red-500"
                            }`}
                          >
                            {formatCurrency(ad.commission)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {!isLoading && sortedAds.length > 0 && totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDetailModal;
